import type { Editor } from "./canvas";
import type { TLShape, TLShapePartial } from "@tldraw/tlschema";
import { isSource, sourceMeters } from "./source-scale";
import { isPdfPageSwap } from "./pdf-page-scale";

export function installSourceBehavior(editor: Editor) {
  // Pinned SDK 5.4.2 exposes these runtime helpers; its public declaration omits
  // @internal methods. Keep this compatibility boundary in one module.
  const sdk = editor as Editor & {
    isReplayingHistory(): boolean;
    getResizeShapePartial(
      shape: TLShape,
      scale: { x: number; y: number },
      options: Record<string, unknown>,
    ): TLShapePartial | null;
  };
  let adjusting = false;
  const before = editor.sideEffects.registerBeforeChangeHandler(
    "shape",
    (prev, next, source) => {
      if (source !== "user" || sdk.isReplayingHistory() || adjusting)
        return next;
      // Legacy attached traces stay at their base origin; page-owned paper moves freely.
      if (
        prev.type === "trace" &&
        next.type === "trace" &&
        prev.parentId === next.parentId &&
        !prev.parentId.startsWith("page:")
      )
        return { ...next, x: prev.x, y: prev.y, rotation: prev.rotation };
      if (prev.type === "mark" && next.parentId !== prev.parentId)
        return { ...next, parentId: prev.parentId, x: prev.x, y: prev.y };
      if (prev.type === "image" && next.type === "image") {
        if (prev.props.assetId !== next.props.assetId) {
          const previousAsset = prev.props.assetId
            ? editor.getAsset(prev.props.assetId)
            : null;
          const nextAsset = next.props.assetId
            ? editor.getAsset(next.props.assetId)
            : null;
          if (
            isPdfPageSwap(
              previousAsset,
              nextAsset,
              JSON.stringify(next.props.crop) !== JSON.stringify(prev.props.crop),
            )
          )
            return next;
          return {
            ...next,
            meta: {
              ...next.meta,
              scaleReview: true,
              metersPerUnit: 0,
              calibrationWidth: next.props.w,
              calibration: "Source replaced · review scale and trace alignment",
            },
          };
        }
        if (JSON.stringify(next.props.crop) !== JSON.stringify(prev.props.crop))
          return {
            ...next,
            meta: {
              ...next.meta,
              metersPerUnit: Number(prev.meta.metersPerUnit ?? 0),
              calibrationWidth:
                (Number(prev.meta.calibrationWidth ?? prev.props.w) *
                  next.props.w) /
                prev.props.w,
            },
          };
        if (
          sourceMeters(editor, prev) > 0 &&
          Math.abs(next.props.w / prev.props.w - next.props.h / prev.props.h) >
            0.0001
        )
          return prev;
      }
      if (
        prev.type === "sheet" &&
        next.type === "sheet" &&
        prev.props.source &&
        prev.props.w !== next.props.w
      )
        return {
          ...next,
          props: {
            ...next.props,
            metersPerUnit:
              (prev.props.metersPerUnit * prev.props.w) / next.props.w,
          },
        };
      return next;
    },
  );
  const after = editor.sideEffects.registerAfterChangeHandler(
    "shape",
    (prev, next, source) => {
      if (
        source !== "user" ||
        sdk.isReplayingHistory() ||
        adjusting ||
        !isSource(prev) ||
        !isSource(next) ||
        prev.type === "trace" ||
        (prev.type === "sheet" && !prev.props.source)
      )
        return;
      if (
        prev.type === "image" &&
        next.type === "image" &&
        JSON.stringify(prev.props.crop) !== JSON.stringify(next.props.crop)
      ) {
        const dx = next.x - prev.x,
          dy = next.y - prev.y,
          c = Math.cos(prev.rotation),
          sn = Math.sin(prev.rotation);
        adjusting = true;
        try {
          editor.updateShapes(
            editor.getSortedChildIdsForParent(next.id).map((id) => {
              const child = editor.getShape(id)!;
              return {
                id,
                type: child.type,
                x: child.x - dx * c - dy * sn,
                y: child.y + dx * sn - dy * c,
              };
            }),
          );
        } finally {
          adjusting = false;
        }
        return;
      }
      if (prev.props.w === next.props.w) return;
      const ratio = next.props.w / prev.props.w;
      const descendants: TLShape[] = [];
      const visit = (id: TLShape["id"]) => {
        for (const child of editor.getSortedChildIdsForParent(id)) {
          const s = editor.getShape(child)!;
          descendants.push(s);
          visit(s.id);
        }
      };
      visit(next.id);
      adjusting = true;
      try {
        const changes: TLShapePartial[] = descendants.map((s) => {
          const base = {
            id: s.id,
            type: s.type,
            x: s.x * ratio,
            y: s.y * ratio,
          };
          if (s.type === "mark")
            return {
              ...base,
              type: "mark",
              props: {
                points: s.props.points.map((p) => ({
                  x: p.x * ratio,
                  y: p.y * ratio,
                })),
              },
            };
          if (s.type === "trace")
            return {
              ...base,
              type: "trace",
              props: { w: s.props.w * ratio, h: s.props.h * ratio },
            };
          const partial = sdk.getResizeShapePartial(
            s,
            { x: ratio, y: ratio },
            {
              scaleOrigin: editor
                .getShapePageTransform(s)!
                .applyToPoint({ x: 0, y: 0 }),
              isAspectRatioLocked: true,
            },
          );
          return { ...partial, ...base } as TLShapePartial;
        });
        editor.updateShapes(changes);
      } finally {
        adjusting = false;
      }
    },
  );
  const remove = editor.sideEffects.registerBeforeDeleteHandler(
    "shape",
    (shape, source) => {
      if (source !== "user" || sdk.isReplayingHistory()) return;
      const traces = editor
        .getSortedChildIdsForParent(shape.id)
        .filter((id) => editor.getShape(id)?.type === "trace");
      if (
        traces.length &&
        !window.confirm(
          `Delete this source and ${traces.length} attached trace(s)? You can undo this.`,
        )
      )
        return false;
    },
  );
  return () => {
    before();
    after();
    remove();
  };
}
